import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { FileSection, type PreviewFile } from "./ImportMarkersDialog";
import { NOISE_BASE } from "./NoiseAnalyticsDialog";
import { toast } from "./toast";

/**
 * Noise Analytics → proofing: every flag you kept (Y) or added, not yet imported, comes in as a picked manual
 * flag exactly as an Audition marker export would. The hashtag sets the type, and the quoted words are found
 * and highlighted on the manuscript. The preview is the marker import's own plan, so what's added is what's
 * shown here. Importing again adds nothing twice.
 */
export function NoiseImportDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [plan, setPlan] = useState<{ files: PreviewFile[]; held: number } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<{ files: PreviewFile[]; held: number }>(`${NOISE_BASE}/${projectId}/import/preview`, { method: "POST" })
      .then(setPlan)
      .catch((e) => { toast((e as Error).message || "Couldn't read the flags", "error"); onClose(); });
  }, [projectId]);        // eslint-disable-line react-hooks/exhaustive-deps

  const all = (plan?.files ?? []).flatMap((f) => f.markers);
  const toAdd = all.filter((m) => m.status === "new").length;
  const already = all.filter((m) => m.status === "duplicate").length;
  const unreadable = all.filter((m) => m.status === "error").length;

  async function run() {
    setBusy(true);
    try {
      const r = await api<{ created: number; already: number; files: number; held: number }>(
        `${NOISE_BASE}/${projectId}/import`, { method: "POST" });
      qc.invalidateQueries({ queryKey: ["noise", projectId] });
      qc.invalidateQueries({ queryKey: ["noise-file", projectId] });
      qc.invalidateQueries({ queryKey: ["pickups", projectId] });
      qc.invalidateQueries({ queryKey: ["history"] });
      toast(r.created
        ? `Added ${r.created} flag${r.created === 1 ? "" : "s"} to ${r.files} file${r.files === 1 ? "" : "s"} in proofing`
        : "Nothing new to add: those flags are already in proofing", r.created ? "success" : "info");
      onClose();
    } catch (e) {
      toast((e as Error).message || "The import didn't go through", "error");
      setBusy(false);
    }
  }

  return (
    <div className="rfs-scrim" onClick={() => { if (!busy) onClose(); }}>
      <div className="rfs wide aim" role="dialog" aria-modal="true" aria-labelledby="nim-title" onClick={(e) => e.stopPropagation()}>
        <div className="rfs-head">
          <div>
            <strong id="nim-title">Import into proofing</strong>
            <span>
              Every flag you kept or added comes in as a flag on its file, picked up for the Pickups Package, just as
              your Audition markers did. Its hashtag sets the type, and the words it quotes are highlighted on the manuscript.
            </span>
          </div>
          {plan && (
            <div className="rfs-chips">
              <span className="rfs-chip ok">{toAdd} to add</span>
              {already > 0 && <span className="rfs-chip mute">{already} already there</span>}
              {unreadable > 0 && <span className="rfs-chip warn">{unreadable} can't be read</span>}
            </div>
          )}
        </div>
        <div className="aim-body">
          {!plan ? (
            <div className="aim-wait"><span className="spinner" style={{ width: 16, height: 16 }} /> Reading your flags…</div>
          ) : plan.files.length === 0 ? (
            <p className="aim-help">Nothing to import yet: flags you keep (Y) or add in a review show up here.</p>
          ) : (
            plan.files.map((f) => <FileSection key={f.chapter_id ?? f.name} file={f} />)
          )}
        </div>
        <div className="rfs-foot">
          <div className="rfs-foot-text">
            {plan && plan.held > 0
              ? <em className="warn">{plan.held} flag{plan.held === 1 ? " is" : "s are"} held back: {plan.held === 1 ? "its file was" : "their files were"} replaced after scoring.</em>
              : <em>Importing again won't add anything twice.</em>}
          </div>
          <div className="rfs-actions">
            <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn primary" disabled={!toAdd || busy} onClick={run}>
              {busy ? "Adding…" : toAdd ? `Add ${toAdd} flag${toAdd === 1 ? "" : "s"}` : "Add flags"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
